import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useQueryReportingSolutionsConfigs, useQueryReportingSolutionsConfigsStatuses } from 'reportingSolution/api';
import { useQueryAttributionConfigStatuses, useQueryIntegratedAttributions } from 'src/attributionModelV2/api';
import { useQueryBudgetOptimiserConfigStatuses, useQueryBudgetOptimisers } from 'src/budgetOptimiserV2/api';
import { useQueryEnhancedSignalsConfigs } from 'src/biddingSignals/api';
import { type CirrusCollection, type CirrusSourceOption } from 'shared/copilot/cirrusSource';
import { buildAttributionModelCirrusOptions, buildBiddingSignalCirrusOptions, buildBudgetOptimiserCirrusOptions, buildRecommendationsCirrusOption, buildReportingSolutionCirrusOptions, sortCirrusSourceOptions, toConfigStatusMap } from 'shared/copilot/utils/cirrusSourceOptions';
export interface CirrusSourceGroup {
  collection: CirrusCollection;
  options: CirrusSourceOption[];
  isLoading: boolean;
}
export interface UseCopilotCirrusSourceOptionsResult {
  /** Config-less Recommendations leaf — always selectable. */
  recommendations: CirrusSourceOption;
  reportingSolutions: CirrusSourceGroup;
  attributionModels: CirrusSourceGroup;
  budgetOptimisers: CirrusSourceGroup;
  biddingSignals: CirrusSourceGroup;
  /** Flat list for resolving a picked option id back to its spec. */
  allOptions: CirrusSourceOption[];
  /**
   * True while any config-backed Cirrus group is still loading. Reconcile and
   * the picker must wait on this — a hydrated Cirrus id from attribution, budget
   * optimiser, or bidding signals would otherwise look missing in a partial
   * `allOptions` and get cleared from picker + agent.state.
   */
  isLoading: boolean;
}

/**
 * Cirrus data sources for the Copilot picker, read entirely from Alvie's own config
 * lists. Flow is never asked to discover them — Alvie already knows the collection,
 * config id and report ids for every source it can offer.
 */
export const useCopilotCirrusSourceOptions = (clientId, recommendationsLabel) => {
  const $ = _c(39);
  const reportingSolutionsQuery = useQueryReportingSolutionsConfigs(clientId);
  const attributionModelsQuery = useQueryIntegratedAttributions(clientId);
  const budgetOptimisersQuery = useQueryBudgetOptimisers(clientId);
  const biddingSignalsQuery = useQueryEnhancedSignalsConfigs(clientId);
  const reportingSolutionStatusesQuery = useQueryReportingSolutionsConfigsStatuses(clientId);
  const attributionStatusesQuery = useQueryAttributionConfigStatuses(clientId);
  const budgetOptimiserStatusesQuery = useQueryBudgetOptimiserConfigStatuses(clientId);
  let t0;
  if ($[0] !== recommendationsLabel) {
    t0 = buildRecommendationsCirrusOption(recommendationsLabel);
    $[0] = recommendationsLabel;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const recommendations = t0;
  let t1;
  if ($[2] !== reportingSolutionStatusesQuery.data || $[3] !== reportingSolutionsQuery.data) {
    t1 = sortCirrusSourceOptions(buildReportingSolutionCirrusOptions(reportingSolutionsQuery.data ?? [], toConfigStatusMap(reportingSolutionStatusesQuery.data)));
    $[2] = reportingSolutionStatusesQuery.data;
    $[3] = reportingSolutionsQuery.data;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== reportingSolutionsQuery.isLoading || $[6] !== t1) {
    t2 = {
      collection: "standard_reports",
      options: t1,
      isLoading: reportingSolutionsQuery.isLoading
    };
    $[5] = reportingSolutionsQuery.isLoading;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const reportingSolutions = t2;
  let t3;
  if ($[8] !== attributionModelsQuery.data || $[9] !== attributionStatusesQuery.data) {
    t3 = sortCirrusSourceOptions(buildAttributionModelCirrusOptions(attributionModelsQuery.data ?? [], toConfigStatusMap(attributionStatusesQuery.data)));
    $[8] = attributionModelsQuery.data;
    $[9] = attributionStatusesQuery.data;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== attributionModelsQuery.isLoading || $[12] !== t3) {
    t4 = {
      collection: "attribution",
      options: t3,
      isLoading: attributionModelsQuery.isLoading
    };
    $[11] = attributionModelsQuery.isLoading;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const attributionModels = t4;
  let t5;
  if ($[14] !== budgetOptimiserStatusesQuery.data || $[15] !== budgetOptimisersQuery.data) {
    t5 = sortCirrusSourceOptions(buildBudgetOptimiserCirrusOptions(budgetOptimisersQuery.data ?? [], toConfigStatusMap(budgetOptimiserStatusesQuery.data)));
    $[14] = budgetOptimiserStatusesQuery.data;
    $[15] = budgetOptimisersQuery.data;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== budgetOptimisersQuery.isLoading || $[18] !== t5) {
    t6 = {
      collection: "bo",
      options: t5,
      isLoading: budgetOptimisersQuery.isLoading
    };
    $[17] = budgetOptimisersQuery.isLoading;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const budgetOptimisers = t6;
  let t7;
  if ($[20] !== biddingSignalsQuery.data) {
    t7 = sortCirrusSourceOptions(buildBiddingSignalCirrusOptions(biddingSignalsQuery.data ?? []));
    $[20] = biddingSignalsQuery.data;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== biddingSignalsQuery.isLoading || $[23] !== t7) {
    t8 = {
      collection: "enhanced_signals",
      options: t7,
      isLoading: biddingSignalsQuery.isLoading
    };
    $[22] = biddingSignalsQuery.isLoading;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const biddingSignals = t8;
  let t9;
  if ($[25] !== attributionModels.options || $[26] !== biddingSignals.options || $[27] !== budgetOptimisers.options || $[28] !== recommendations || $[29] !== reportingSolutions.options) {
    t9 = [recommendations, ...reportingSolutions.options, ...attributionModels.options, ...budgetOptimisers.options, ...biddingSignals.options];
    $[25] = attributionModels.options;
    $[26] = biddingSignals.options;
    $[27] = budgetOptimisers.options;
    $[28] = recommendations;
    $[29] = reportingSolutions.options;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const allOptions = t9;
  const isLoading = reportingSolutions.isLoading || attributionModels.isLoading || budgetOptimisers.isLoading || biddingSignals.isLoading;
  let t10;
  if ($[31] !== allOptions || $[32] !== attributionModels || $[33] !== biddingSignals || $[34] !== budgetOptimisers || $[35] !== isLoading || $[36] !== recommendations || $[37] !== reportingSolutions) {
    t10 = {
      recommendations,
      reportingSolutions,
      attributionModels,
      budgetOptimisers,
      biddingSignals,
      allOptions,
      isLoading
    };
    $[31] = allOptions;
    $[32] = attributionModels;
    $[33] = biddingSignals;
    $[34] = budgetOptimisers;
    $[35] = isLoading;
    $[36] = recommendations;
    $[37] = reportingSolutions;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
};